import { updateHistory } from '@/libraries/api/faces'
import { DetectionMerger } from '@/libraries/util/detectionMerger'

const MAX_HISTORIES_TO_CACHE = 10

export const facesModule = {
  namespaced: true,
  state: {
    /**
     * A map of profile history arrays
     */
    cachedHistories: {},
    /**
     * A list of history UUIDs, for better reactivity.
     */
    cachedHistoryUUIDs: [],
    /**
     * A map of profile track arrays
     */
    cachedTracks: {},
  },
  mutations: {
    /**
     * Remove a specific event from the cached history.
     * The `data` object has two keys: `frontendFaceUUID` describing the history to inspect and a `detection` object to remove.
     */
    removeEventFromCachedHistory (state, data) {
      if (data.frontendFaceUUID && state.cachedHistories[data.frontendFaceUUID]) {
        const index = state.cachedHistories[data.frontendFaceUUID].findIndex(detection => detection.detection_id === data.detectionId)
        if (index >= 0) {
          const detectionForRemoval = state.cachedHistories[data.frontendFaceUUID][index]
          if (detectionForRemoval.merged && detectionForRemoval.merged.length >= 1) {
            // merged detections
            const cache = [...state.cachedHistories[data.frontendFaceUUID]]
            const topmostDetection = detectionForRemoval.merged[0]
            detectionForRemoval.merged.splice(0, 1)
            topmostDetection.merged = detectionForRemoval.merged
            cache[index] = topmostDetection
            state.cachedHistories[data.frontendFaceUUID] = cache
          } else {
            // single detection
            state.cachedHistories[data.frontendFaceUUID].splice(index, 1)
          }
        }
        // remove the UUID from history so that history is loaded again after navigating away
        const profileUUIDIndex = state.cachedHistoryUUIDs.findIndex(uuid => uuid === data.frontendFaceUUID)
        state.cachedHistoryUUIDs.splice(profileUUIDIndex, 1)
        state.cachedHistoryUUIDs.push(data.frontendFaceUUID)
      }
    },
    /**
     * Caches a history for a given uuid.
     * The `data` object has two keys: `frontendFaceUUID` describing the history to inspect and a `detection` object to remove.
     **/
    addHistoryToCache (state, data) {
      state.cachedHistories[data.frontendFaceUUID] = data.detections
      state.cachedTracks[data.frontendFaceUUID] = data.tracks
      state.cachedHistoryUUIDs.push(data.frontendFaceUUID)
    },
    /* Removes the oldest cached history. */
    removeOldestHistoryFromCache (state) {
      const uuidOfOldestCache = state.cachedHistoryUUIDs[0]
      state.cachedHistories[uuidOfOldestCache] = []
      state.cachedTracks[uuidOfOldestCache] = []
      state.cachedHistoryUUIDs.splice(0, 1)
    },
  },
  getters: {
    /** Returns the profile that the user is viewing, based on the route. */
    currentProfileUUID (state, getters, rootState) {
      return rootState.route.params.id
    },
    /** The entire history - including events that may be hidden. */
    entireHistory (state, getters) {
      const uuid = getters.currentProfileUUID
      const hasHistory = state.cachedHistoryUUIDs.includes(uuid)
      // NOTE: This check is explicit, so VueJS will watch `hasHistory`.
      //       Without it, deep watching won't catch history changes and
      //       the UI won't react to the addition of a history.
      //       Don't combine this with the final return statement.
      if (!hasHistory) {
        return []
      }
      return state.cachedHistories[uuid]
    },
    entireTracks (state, getters) {
      const uuid = getters.currentProfileUUID
      const hasTracks = state.cachedHistoryUUIDs.includes(uuid)
      // NOTE: This check is explicit, so VueJS will watch `hasHistory`.
      //       Without it, deep watching won't catch history changes and
      //       the UI won't react to the addition of a history.
      //       Don't combine this with the final return statement.
      if (!hasTracks) {
        return []
      }
      return state.cachedTracks[uuid]
      // return state.cachedTracks[getters.currentProfileUUID] || []
    },
    /** Determines if the expanded or collapsed history is visible. */
    /** A getter exposing the number of the unfiltered history length. */
    entireHistoryLength (state, getters) {
      return getters.entireHistory.length
    },
  },
  actions: {
    /** Updates the history from the API, consolidates events, then updates the store. */
    fetchUpdatedHistory ({ state, commit, getters }) {
      const frontendFaceUUID = getters.currentProfileUUID
      if (!frontendFaceUUID) {
        console.debug(`No frontend_face_uuid. Skipping history update.`)
        return
      }

      commit('incrementRequestCountForServiceNamed', 'history', { root: true })
      updateHistory(frontendFaceUUID)
        .then(res => {
          const results = res.data.faces_matched
          const tracks = res.data.tracks
          const resultsAndTracks = mergeResultsAndTracks(results, tracks)
          const merger = new DetectionMerger()
          if (!merger) {
            console.debug('DetectionMerger not detected. Cannot merge detections.')
          }

          const detections = merger ? merger.groupedDetectionsFromDetections(resultsAndTracks) : resultsAndTracks
          console.log('merged detections:')
          console.dir(detections)
          const payload = { frontendFaceUUID, detections }

          if (state.cachedHistoryUUIDs.length >= MAX_HISTORIES_TO_CACHE) {
            commit('removeOldestHistoryFromCache')
          }

          commit('addHistoryToCache', payload)
        })
        .catch(error => {
          console.error('Error loading histroy: ', error)
        })
        .then(
          function () {
            commit('decrementRequestCountForServiceNamed', 'history', {
              root: true,
            })
          },
        )
    },
  },
}

function mergeResultsAndTracks(results, tracks) {
  if (!tracks) {
    return results
  }
  // add in tracks
  // if (this.entireTracks && Array.isArray(this.entireTracks) && this.entireTracks.length) {
  // TODO: Move this to detection merger, or something...
  console.log(`Have ${tracks.length} tracks`)
  console.dir(tracks)
  /*
  [0]:
  all_frontend_face_uuids: "{23d32300-f2bc-4631-ac2f-bb31d81332eb}"
  steps: Array(8)
  0: {camera_description: "Center: lobby exit west (ER)", camera_uuid: "37f689d3-08ef-45e3-b437-d4c4be648502", matched_frontend_face_uuid_index: null, ts: "2021-01-04T05:41:08.839957"}
  1: {camera_description: "Center: lobby exit west (ER)", camera_uuid: "37f689d3-08ef-45e3-b437-d4c4be648502", matched_frontend_face_uuid_index: null, ts: "2021-01-04T05:41:09.87433"}
  2: {camera_description: "Center: lobby exit west (ER)", camera_uuid: "37f689d3-08ef-45e3-b437-d4c4be648502", matched_frontend_face_uuid_index: null, ts: "2021-01-04T05:41:10.900613"}
  3: {camera_description: "Center: lobby exit west (ER)", camera_uuid: "37f689d3-08ef-45e3-b437-d4c4be648502", matched_frontend_face_uuid_index: null, ts: "2021-01-04T05:41:11.921009"}
  4: {camera_description: "Center: lobby exit west (ER)", camera_uuid: "37f689d3-08ef-45e3-b437-d4c4be648502", matched_frontend_face_uuid_index: null, ts: "2021-01-04T05:41:12.84177"}
  5: {camera_description: "Center: lobby exit west (ER)", camera_uuid: "37f689d3-08ef-45e3-b437-d4c4be648502", matched_frontend_face_uuid_index: null, ts: "2021-01-04T05:41:13.862846"}
  6:
  camera_description: "Center: lobby exit west (ER)"
  camera_uuid: "37f689d3-08ef-45e3-b437-d4c4be648502"
  matched_frontend_face_uuid_index: 0
  ts: "2021-01-04T05:41:14.885799"
  __proto__: Object
  7: {camera_description: "Center: lobby exit west (ER)", camera_uuid: "37f689d3-08ef-45e3-b437-d4c4be648502", matched_frontend_face_uuid_index: null, ts: "2021-01-04T05:41:15.921671"}
  length: 8
  __proto__: Array(0)
  track_end: "2021-01-04T05:41:15.921671"
  track_start: "2021-01-04T05:41:08.839957"
  track_uuid: "bf613d2d-d0f6-4ed5-ae9c-7b0a74777c1e"
  */
  console.log(`For reference, here's the first match:`)
  if (results && results.length) {
    console.dir(results[0])
  } else {
    console.log('Sorry, empty results :(')
  }
  /*
  camera_description: "Center: lobby exit west (ER)"
  camera_uuid: "37f689d3-08ef-45e3-b437-d4c4be648502"
  captured_at: "2021-01-04T05:41:14.885799"
  detection_id: "5endw6WaeO3lgR72"
  face_confidence: 0.28775206208229065
  face_height: 372
  frame_uri: "https://s3.amazonaws.com/owal-clips/faces/detected/91dee1d0-420c-4726-afd7-b36d5be09570/2021/01/81db3edb-eea6-4d9b-8d8d-64953cf683f0.jpg"
  frontend_face_uuid: "23d32300-f2bc-4631-ac2f-bb31d81332eb"
  is_suppressed: null
  mask_label: "mask_ok"
  mask_label_confidence: 0.9992079734802246
  match_confidence: 0.9802915324200648
  match_confidence_level: 2
  runtime_face_uuid: null
  */
  // Let's unpack the track steps, re-group by same-camera-similar-time. assume no overlapping tracks
  const trackSteps = []
  tracks.forEach(track => {
    track.steps.forEach(step => {
      const trackStep = {
        // convert to as similar to detection as possible
        camera_description: step.camera_description,
        camera_uuid: step.camera_uuid,
        captured_at: step.captured_at + (step.captured_at.endsWith('Z') ? '' : 'Z'),
        detection_id: track.track_uuid + '_' + step.captured_at,
        // face_confidence
        // face_height
        // frame_uri
        // frontend_face_uuid
        // is_suppressed: null
        // mask_label
        // mask_label_confidence
        // match_confidence
        // match_confidence_level
        // runtime_face_uuid: null
        track_uuid: track.track_uuid,
        track_start: track.track_start,
        track_end: track.track_end,
        is_track_step: true,
        frontend_face_uuid: track.frontend_face_uuid,
      }
      if (step.matched_frontend_face_uuid_index !== null) {
        trackStep['matched_frontend_face_uuid'] = track.all_frontend_face_uuids[step.matched_frontend_face_uuid_index]
      }
      trackSteps.push(trackStep)
    })
  })
  console.log('results before merge:')
  console.dir(results)
  console.log('trackSteps before merge:')
  console.dir(trackSteps)
  const mergedResults = results.map(x => ({ ...x, is_detection: true })).concat(trackSteps).sort((a, b) => new Date(b.captured_at) - new Date(a.captured_at))
  console.log('merged:')
  console.dir(mergedResults)
  return mergedResults
  // }
}
